/**
 * Куда вести по клику на уведомление.
 *
 * Ссылку кладёт бэкенд полем `url`, и для трекера она абсолютная
 * (`https://tracker.vpit.dev/issues/DEV-1`): её собирает сам трекер по своему
 * `TRACKER_BASE_URL`. Свой же абсолютный адрес переводим в путь роутера, иначе
 * клик перезагружал бы приложение целиком ради перехода на соседний экран.
 * Чужой абсолютный адрес открываем новой вкладкой: `router.push` принял бы его
 * за внутренний путь и увёл в никуда.
 */
import type { RouteLocationRaw, Router } from 'vue-router'

import type { Notification } from './types'

const isAbsolute = (url: string) => /^https?:\/\//i.test(url)

/** Ссылка уведомления с фолбэком на metadata — там её оставляли до DEV-1961. */
export function notificationUrl(notification: Notification): string {
  const fromMeta = (notification.metadata ?? {}).url

  return notification.url || (typeof fromMeta === 'string' ? fromMeta : '')
}

/** Внутренний путь, если ссылка ведёт в этот же трекер. Иначе null. */
export function internalPath(url: string): string | null {
  if (!url) return null

  if (!isAbsolute(url)) return url.startsWith('/') ? url : `/${url}`

  try {
    const parsed = new URL(url)

    return parsed.origin === window.location.origin ? parsed.pathname + parsed.search : null
  } catch {
    return null
  }
}

export function buildNotificationRoute(notification: Notification): RouteLocationRaw | null {
  const url = notificationUrl(notification)
  if (!url) return null

  return internalPath(url) ?? url
}

/** Переход по уведомлению. Возвращает true, если куда-то ушли. */
export function openNotificationTarget(notification: Notification, router: Router): boolean {
  const url = notificationUrl(notification)
  if (!url) return false

  const path = internalPath(url)

  if (path) {
    void router.push(path)

    return true
  }

  window.open(url, '_blank', 'noopener')

  return true
}
